import { OptionalTierLock } from '@trimble/tiering';
import { TieringBehaviors } from '@trimble/types';
import type { ProjectDataRepository } from '@trimble/project-data-repository';
import { createMemo, Show, type Accessor, type JSX } from 'solid-js';
import {
  assertSingleSelectOperationsConfigured,
  type ProjectDataPanelOperations,
  type ProjectDataPanelSectionData,
  type ProjectDataSectionConfig,
  type ProjectDataSectionDefinition,
  type ProjectDataSectionDefinitionMap,
  resolveProjectDataSectionConfig,
} from './project-data-section-registry';

export interface ConfiguredProjectDataSectionProps {
  readonly section: ProjectDataSectionConfig;
  readonly definition: ProjectDataSectionDefinition;
  readonly repository: Accessor<ProjectDataRepository | undefined>;
  readonly operations: Accessor<ProjectDataPanelOperations | undefined>;
  readonly sectionData: Accessor<ProjectDataPanelSectionData | undefined>;
}

// Section ids come from host config as plain strings, so only own keys count
// (rejects inherited names such as "constructor").
function readSectionEntry<TMap extends object>(
  map: TMap | undefined,
  sectionId: string
): TMap[keyof TMap] | undefined {
  if (!map || !Object.prototype.hasOwnProperty.call(map, sectionId)) {
    return undefined;
  }
  return map[sectionId as keyof TMap];
}

export const ConfiguredProjectDataSection = (
  props: ConfiguredProjectDataSectionProps
): JSX.Element => {
  const resolvedConfig = createMemo(() =>
    resolveProjectDataSectionConfig(props.section, props.definition)
  );

  const RenderedSection = (): JSX.Element => {
    const repository = props.repository();
    const operationsMap = props.operations();
    const sectionId = props.section.id;
    const definition = props.definition;

    if (!repository) {
      throw new Error(
        `Project data section "${sectionId}" requires a repository.`
      );
    }

    const operations = readSectionEntry(operationsMap, sectionId);
    if (!operations) {
      throw new Error(
        `Project data section "${sectionId}" has no operations configured.`
      );
    }

    const dataAccessor = (): unknown =>
      readSectionEntry(props.sectionData(), sectionId);

    if (definition.requiresData && dataAccessor() === undefined) {
      throw new Error(
        `Project data section "${sectionId}" has no data configured.`
      );
    }

    const config = resolvedConfig();
    assertSingleSelectOperationsConfigured(
      sectionId,
      config.multiSelect,
      operations
    );

    return definition.render({
      repository,
      config,
      data: dataAccessor,
      operations,
    });
  };

  return (
    <Show when={resolvedConfig().tiering} keyed fallback={<RenderedSection />}>
      {(tiering) => (
        <OptionalTierLock
          preserveWidth={true}
          options={{
            disabledBehavior: TieringBehaviors.Restricted,
            showBadge: true,
            featureName: tiering,
          }}
        >
          <RenderedSection />
        </OptionalTierLock>
      )}
    </Show>
  );
};

export function getConfiguredSectionDefinition(
  section: ProjectDataSectionConfig,
  sectionDefinitions: ProjectDataSectionDefinitionMap
): ProjectDataSectionDefinition {
  const definition = sectionDefinitions[section.id];
  if (!definition) {
    throw new Error(
      `Project data section "${section.id}" has no definition. Pass sectionDefinitions from the composition root.`
    );
  }
  return definition;
}
